import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import ElementaryProgressReportDao from "../../../dao/ElementaryProgressReportDao";
import { subjectIdToContentAreaId } from "../../../utils/SchoolAdmin/translations";
import { enrichItemsByGrade } from "./enrichDiffItems";
import {
    buildDiffRosterKey,
    collectGroupCandidates,
    collectHomeroomCandidates,
    indexDiffPayloads
} from "./indexRosterDiffRatings";
import { snapshotWithSavedGrid } from "./buildDiffStudentSavePayload";

/**
 * Loads per-student getStudentRatings for differentiated bulk rating.
 * Homeroom: only differentiated: true. Groups: all selected students, empty diffs dropped.
 */
const useRosterDiffRatings = ({ mode, existingRatingsData, groupData, selectedEpr, selectedTemplate, token }) => {
    const [indexed, setIndexed] = useState(() => emptyIndex());
    const [loading, setLoading] = useState(false);

    const rosterKey = useMemo(
        () => buildDiffRosterKey({ mode, groupData, existingRatingsData }),
        [mode, groupData, existingRatingsData]
    );

    const load = useCallback(
        async ({ silent } = {}) => {
            if (!selectedTemplate || !token) {
                setIndexed(emptyIndex());
                return;
            }

            const candidates =
                mode === "GROUPS"
                    ? collectGroupCandidates({ groupData, selectedEpr, selectedTemplate })
                    : collectHomeroomCandidates({ existingRatingsData, selectedEpr, selectedTemplate });

            if (!candidates.length) {
                setIndexed(emptyIndex());
                return;
            }

            // After save, keep the grid mounted. Unmounting mid-typeset used to throw
            // "Cannot read properties of null (reading 'nextSibling')" on MathJax nodes;
            // SafeMathHtml also clears typeset on unmount as a second line of defense.
            if (!silent) setLoading(true);
            try {
                const responses = await Promise.all(
                    candidates.map((candidate) =>
                        ElementaryProgressReportDao({
                            action: "getStudentRatings",
                            gradingCriteriaId: candidate.gradingCriteriaId,
                            gradingProfileStudentId: candidate.gradingProfileStudentId,
                            token
                        }).then((res) => ({
                            candidate,
                            payload: res?.data?.payload || null
                        }))
                    )
                );
                const indexedPayloads = indexDiffPayloads(responses.filter((r) => r.payload));
                let itemsByGrade = indexedPayloads.itemsByGrade;
                const contentAreaKey = subjectIdToContentAreaId[selectedTemplate.subjectId];
                if (indexedPayloads.grades.length && contentAreaKey) {
                    const reportable = await ElementaryProgressReportDao({
                        action: "eprReportableByContentAreaAndGrades",
                        contentAreaKey,
                        gradeLevels: indexedPayloads.grades.join(","),
                        token
                    });
                    if (reportable?.data?.payload) {
                        itemsByGrade = enrichItemsByGrade(itemsByGrade, reportable.data.payload);
                    }
                }
                setIndexed({ ...indexedPayloads, itemsByGrade });
            } catch (error) {
                console.error(error);
                toast.error("Unable to load differentiated ratings.");
                if (!silent) setIndexed(emptyIndex());
            } finally {
                setLoading(false);
            }
        },
        // rosterKey stands in for groupData / existingRatingsData identity
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mode, rosterKey, selectedEpr, selectedTemplate, token]
    );

    useEffect(() => {
        load();
    }, [load]);

    const reload = useCallback(() => load({ silent: true }), [load]);

    const commitSavedStudents = useCallback((studentKeys, ratings, comments) => {
        if (!studentKeys?.length) return;
        setIndexed((prev) => {
            const snapshots = { ...prev.snapshots };
            studentKeys.forEach((studentKey) => {
                const snap = snapshots[studentKey];
                if (!snap) return;
                snapshots[studentKey] = snapshotWithSavedGrid(snap, ratings, comments, studentKey);
            });
            return { ...prev, snapshots };
        });
    }, []);

    return { ...indexed, loading, reload, commitSavedStudents, setIndexed };
};

const emptyIndex = () => ({
    grades: [],
    itemsByGrade: {},
    studentsByGrade: {},
    ratings: {},
    comments: {},
    snapshots: {},
    students: []
});

export default useRosterDiffRatings;
